"use client";

import { useContext, useState, useEffect } from "react";
import { usePathname } from "next/navigation";

import { QRPreviewStyle, QRPreviewData } from ".";
import { PreviewToggle } from "@/components/PreviewToggle";

import { QRPreviewProps } from "./types";

import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";

import { useQR, useQRInitState } from "@/hooks/qr";

import { getPageIndexFromUrl, QR_PAGE_INDEX } from "@/utils/qrEditorNavigatorHelper";
import { omittedValues } from "@/utils/qrEditorHelper";

import "./styles.scss";

export const QRPreview = ({
  previewHome,
  previewTranslations,
  previewButtonsTranslations,
  language,
  modalData,
  defaultPreviewTitle,
}: QRPreviewProps) => {
  const pathname = usePathname();

  const qrEditorContext = useContext<QREditorContextProps<any>>(QREditorContext);

  const [isQRActivated, setIsQRActivated] = useState(false);
  const [previewType, setPreviewType] = useState<"qr" | "data" | undefined>(
    qrEditorContext?.qrData?.currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "qr" : "data"
  );

  const { newData } = useQR();
  const isQRInit = useQRInitState(qrEditorContext);

  const setPreviewTypeToggle = (type) => setPreviewType(type);

  useEffect(() => {
    const currentPageIndex = getPageIndexFromUrl(pathname);
    if (currentPageIndex === QR_PAGE_INDEX.DESIGN_QR) {
      setPreviewType("qr");
    } else {
      setPreviewType("data");
    }
  }, [pathname]);

  useEffect(() => {
    //QR is active if form methods are true and there's no extra validations errors in the form
    const activeQR = qrEditorContext?.qrData.isValidForm && !qrEditorContext?.validationError;
    setIsQRActivated(activeQR);
  }, [qrEditorContext]);

  useEffect(() => {
    if (newData) {
      qrEditorContext.setQrData(
        {
          ...qrEditorContext.qrData,
          data: newData.data,
          qrStyle: newData.qrStyle,
        },
        [...omittedValues(qrEditorContext?.qrData.data)]
      );
    }
  }, [newData]);

  return (
    <div
      data-testid="qr-preview"
      className="qr-preview"
    >
      <div className="qr-preview__layout">
        <div className="qr-preview__header">
          <div className="u-spacing-4" />
          {qrEditorContext?.qrData.currentPageIndex > 0 && !isQRInit && (
            <PreviewToggle
              previewType={previewType}
              setPreviewType={setPreviewTypeToggle}
              isQRActivated={isQRActivated}
              previewButtonsTranslations={previewButtonsTranslations}
            />
          )}
        </div>
        <div className="qr-preview__container">
          {previewType === "qr" ? (
            <QRPreviewStyle
              data={qrEditorContext?.qrData}
              modalData={modalData}
              defaultPreviewTitle={defaultPreviewTitle}
            />
          ) : (
            <QRPreviewData
              data={qrEditorContext?.qrData}
              previewHome={previewHome}
              previewTranslations={previewTranslations}
              isQRInit={isQRInit}
              isQRActivated={isQRActivated}
              language={language}
              defaultPreviewTitle={defaultPreviewTitle}
            />
          )}
        </div>
      </div>
    </div>
  );
};
